import { addQueryParameters } from '../queryHelpers';

const breakPoints = [375, 576, 768, 992, 1200, 1440];

export const toNearestBreakPoint = (width?: number, height?: number) => {
  if (!width)
    return {
      width,
      height,
    };

  const breakPointWidth = breakPoints.find(breakPoint => breakPoint >= width) || width;
  const breakPointHeight = height ? height * (breakPointWidth / width) : undefined;

  return {
    width: breakPointWidth,
    height: breakPointHeight,
  };
};

// Source of truth for param types https://github.com/imgix/imgix-url-params/blob/master/dist/parameters.json
export type ImgixOptions = {
  rightCornerMark?: boolean;
  gradient?: boolean;
  dpr?: number;
  blur?: number;
  fit?: Fit;
  w?: number;
  h?: number;
  auto?: Auto | Auto[] | string;
  fm?: Fm;
  crop?: Crop | Crop[];
  q?: number;
  ar?: string;
  padLeft?: number;
};
type Fit = 'clamp' | 'clip' | 'crop' | 'facearea' | 'fill' | 'fillmax' | 'max' | 'min' | 'scale';

type Crop = 'top' | 'bottom' | 'left' | 'right' | 'faces' | 'entropy' | 'edges' | 'focalpoint';

type Fm =
  | 'gif'
  | 'jpg'
  | 'jp2'
  | 'json'
  | 'jxr'
  | 'pjpg'
  | 'mp4'
  | 'png'
  | 'png8'
  | 'png32'
  | 'webp'
  | 'webm'
  | 'blurhash'
  | 'avif';

type Auto = 'enhance' | 'format' | 'compress' | 'true' | 'redeye';

export function toImgix(baseUrl: string, options: ImgixOptions): string {
  if (options.crop && !options.fit) {
    throw new Error('Cannot use crop without fit');
  }
  const entries = Object.entries(options);

  const imgixParams = entries.reduce<Record<string, string | undefined>>((params, entry) => {
    const [key, value] = entry;
    switch (key) {
      case 'gradient': {
        if (!options.gradient) return params;
        return {
          ...params,
          ...createSoftGradient(),
        };
      }
      case 'rightCornerMark': {
        if (!options.rightCornerMark) return params;
        return { ...params, ...createRightCornerMark() };
      }
      case 'padLeft': {
        if (!value) return params;
        const rawWidth = options.w ?? 0;
        const calcHeight = (rawWidth * 9) / 16;

        const { width, height } = toNearestBreakPoint(rawWidth, calcHeight);

        const padWidth = (width || 0) * (1 - parseFloat(value.toString()));

        return {
          ...params,
          'pad-left': padWidth.toString(),
          ar: undefined,
          w: Math.round(width ?? 0).toString(),
          h: Math.round(height ?? 0).toString(),
        };
      }
      default: {
        params[key] = value?.toString();
        return params;
      }
    }
  }, {});
  return addQueryParameters(baseUrl, imgixParams);
}

const SOFT_GRADIENT_URL =
  'aHR0cHM6Ly90djJwbGF5LmltZ2l4Lm5ldC8yMDU4Njg1ZC05MjA3LTQ0ZGQtOTQ0MS1iZGNkZjQyNmIwOTdfZ3JhZGllbnRfYmxhY2tfNTAucGRmP3BhZ2U9MQoK';

function createSoftGradient() {
  return { blend64: SOFT_GRADIENT_URL, bw: '1', bm: 'multiply', ba: 'bottom', bf: 'clip' };
}

function createRightCornerMark() {
  return {
    mask: `https://tv2-dev-snowpiercer.s3.eu-central-1.amazonaws.com/assets/background-mask.png`,
  };
}
